import { defineStore } from 'pinia'

import { defaultJSONOptions } from '@shared/models/proto/constants'
import { Nullable } from '@shared/models/types/primary'
import { httpDelete, post } from '@shared/plugins/api'
import { SessionProvider } from '@shared/plugins/session-provider'
import { LsGlobalKeys, lsSave } from '@shared/utils/helpers/local-storage'

import { PeerProps } from '@protots/auth/peer_props'
import {
  SignIn as SignInDTO,
  SignIn_Provider as SignInProviders,
} from '@protots/auth/request/sign_in'
import { SignIn as SignInRO } from '@protots/auth/response/sign_in'
import { Session } from '@protots/auth/session'
import { User } from '@protots/auth/user'

import { SessionData } from '@protos/auth/session_data_pb'

interface State {
  peerProps: Nullable<PeerProps>
  currentUser: Nullable<User>
  isOnline: boolean
  session: Nullable<Session>
  isFileUploading: boolean
  isAppVisible: boolean
}

const state: () => State = () => {
  return {
    peerProps: SessionProvider.getPeerProps(),
    session: SessionProvider.getStoredSession(),
    currentUser:
      SessionProvider.getStoredSession()?.impersonatedAs ??
      SessionProvider.getStoredSession()?.user ??
      null,
    isOnline: true,
    isFileUploading: false,
    isAppVisible: true,
  }
}

export const useGeneralStore = defineStore('general', {
  state,

  actions: {
    /**
     * @description
     * This is a development-only method
     * Used for automatic sign-in per module
     */
    async signIn(namespace: string) {
      const payload: SignInDTO = {
        provider: SignInProviders.CREDENTIALS,
        identifier: process.env.VUE_APP_API_EMAIL ?? 'admin@adam.test',
        secret: process.env.VUE_APP_API_PASSWORD ?? 'start123',
        pnToken: '',
        emptyDatabase: false,
      }

      const { data: response } = await post(
        '/sign_in',
        SignInDTO.toJson(payload, defaultJSONOptions),
        { namespace }
      )

      const { data } = SignInRO.fromJson(response)

      lsSave(LsGlobalKeys.PeerProps, data?.peerProps)
      this.peerProps = data?.peerProps ?? null
      lsSave(LsGlobalKeys.Crd, data?.authSession)
      if (data?.authSession) {
        this.setSession(data.authSession)
      }
    },

    signOut(namespace: string) {
      httpDelete('/sign_out', { namespace })
    },

    updateSession(data?: SessionData) {
      data?.peerProps && (this.peerProps = data.peerProps)
      data?.authSession && this.setSession(data.authSession)

      const isPatient = Boolean(window.landing)
      if (isPatient && window.landing.data) {
        window.landing.data.peerProps = data?.peerProps
        window.landing.data.authInfo.authSession = data?.authSession
      } else {
        window.sessionData = data ?? new SessionData()
      }
    },

    setSession(session: Session) {
      this.session = session
      this.currentUser = session.impersonatedAs || (session.user as User)
    },

    isMe(userId?: string | number): boolean {
      const currentUserId = this.currentUser?.id
      return !!currentUserId && currentUserId.toString() === userId?.toString()
    },
  },

  getters: {
    apiRequestHeaders(state) {
      return {
        'X-Fingerprint': state.session?.deviceUuid ?? null,
        Authorization: state.session?.accessToken
          ? `Bearer ${state.session?.accessToken}`
          : null,
      }
    },

    chatUserIsMe: (s) => (id?: User['chatUserId']) =>
      s.currentUser?.chatUserId === id,
  },
})
